import type { ComponentProps, ReactElement } from "react";
import { useState } from "react";

import { useSelector } from "react-redux";

import { translate } from "app/filters/i18nFilter";
import { isContactSearchEntryRevealed } from "app/pages/sales-intelligence/sub-modules/contacts-search/utils/isContactSearchEntryRevealed";
import {
    selectIsContactsDisabledByAccount,
    selectIsContactsDisabledByCountry,
    selectIsContactsPendingApproval,
} from "app/pages/sales-intelligence/sub-modules/contacts-tab/store/selectors";

import ContactBaseInfo from "../components/ContactBaseInfo/ContactBaseInfo";
import ContactBaseInfoEmptyState from "../components/ContactBaseInfo/ContactBaseInfoEmptyState";
import TopCompanyGeneralInfo from "../components/TopCompanyGeneralInfo/TopCompanyGeneralInfo";
import TopCompanyInfoWrapper from "../components/TopCompanyInfoWrapper/TopCompanyInfoWrapper";
import TopCompanyOpportunitySpotted from "../components/TopCompanyOpportunitySpotted/TopCompanyOpportunitySpotted";
import type { ITopCompanyItem } from "../types/TopCompany";

import ContactDetailsContainer from "./ContactDetailsContainer";
import {
    StyledCardContainerFlex,
    StyledContactUsSuccessState,
    StyledContactUsSuccessStateWrapper,
    StyledTopCompaniesCardWrapper,
    StyledTopCompanyContactWrapper,
    StyledTopCompanyGeneralInfoWrapper,
    StyledTopCompanyOpportunitySpottedWrapper,
} from "./styles";

interface IProps {
    company: ITopCompanyItem;
    isLoading: boolean;
    isOpportunitySpottedFetching: boolean;
    opportunitySpottedText?: string;
}

type TopCompaniesCardProps = IProps & Pick<ComponentProps<typeof ContactDetailsContainer>, "onRefetchList">;

const TopCompaniesCardContainer = ({
    company,
    isLoading,
    isOpportunitySpottedFetching,
    opportunitySpottedText,
    onRefetchList,
}: TopCompaniesCardProps): ReactElement => {
    const [isShowContactUsSuccessState, setIsShowContactUsSuccessState] = useState(false);

    const isContactsPendingApproval = useSelector(selectIsContactsPendingApproval);
    const isContactsDisabledByAccount = useSelector(selectIsContactsDisabledByAccount);
    const isContactsDisabledByCountry = useSelector(selectIsContactsDisabledByCountry);

    const isContactRevealed = company.selectedContact ? isContactSearchEntryRevealed(company.selectedContact) : false;

    const handleContactUsClick = (): void => {
        setIsShowContactUsSuccessState(true);
    };

    const handleAnimationEnd = (): void => {
        setIsShowContactUsSuccessState(false);
    };

    return (
        <StyledTopCompaniesCardWrapper data-automation="top-companies-card-wrapper">
            <StyledCardContainerFlex>
                <StyledTopCompanyGeneralInfoWrapper data-automation="top-companies.company-info-wrapper">
                    <TopCompanyGeneralInfo company={company} />
                </StyledTopCompanyGeneralInfoWrapper>
                <StyledTopCompanyContactWrapper data-automation="top-companies.contact-info-wrapper">
                    <TopCompanyInfoWrapper title={translate("si.top-companies.contact-details.title")}>
                        {company.selectedContact ? (
                            <ContactBaseInfo
                                isDisabledPrivateData={
                                    isContactsDisabledByCountry ||
                                    ((isContactsDisabledByAccount || isContactsPendingApproval || isLoading) &&
                                        !isContactRevealed)
                                }
                                contact={company.selectedContact}
                            >
                                <ContactDetailsContainer
                                    isDisabledContact={isContactsDisabledByCountry}
                                    isDisabledTooltip={isShowContactUsSuccessState}
                                    selectedContact={company.selectedContact}
                                    onContactUs={handleContactUsClick}
                                    onRefetchList={onRefetchList}
                                />

                                {isShowContactUsSuccessState ? (
                                    <StyledContactUsSuccessStateWrapper onAnimationEnd={handleAnimationEnd}>
                                        <StyledContactUsSuccessState />
                                    </StyledContactUsSuccessStateWrapper>
                                ) : null}
                            </ContactBaseInfo>
                        ) : (
                            <ContactBaseInfoEmptyState />
                        )}
                    </TopCompanyInfoWrapper>
                </StyledTopCompanyContactWrapper>
                <StyledTopCompanyOpportunitySpottedWrapper data-automation="top-companies.agent-wrapper">
                    <TopCompanyInfoWrapper
                        iconName="sparkles-ai-2"
                        title={translate("si.top-companies.opportunity-spotted.title")}
                    >
                        <TopCompanyOpportunitySpotted
                            companyDomain={company.domain}
                            isLoading={isOpportunitySpottedFetching}
                            text={opportunitySpottedText}
                            contactName={company?.selectedContact?.name}
                        />
                    </TopCompanyInfoWrapper>
                </StyledTopCompanyOpportunitySpottedWrapper>
            </StyledCardContainerFlex>
        </StyledTopCompaniesCardWrapper>
    );
};

export default TopCompaniesCardContainer;
